import { Cubit } from "blac-next";
import { ChangeEvent, ReactNode, RefObject } from "react";
import type { DropEvent, FileDropItem } from "react-aria";

export type FileUploadStatus = "uploading" | "uploaded" | "error";
export type FileUploadErrorType = "size" | "type" | "upload";

export interface FileUploadState {
  files: File[];
  fileErrors?: Map<number, { type: FileUploadErrorType; message?: ReactNode }>;
  fileStatuses?: Map<number, FileUploadStatus>;
}

export interface FileUploadControls {
  setStatus: (status: FileUploadStatus) => void;
  setErrorMessage: (message: ReactNode) => void;
  setProgress: (progress: number) => void;
}

export interface FileUploadConfig {
  accept?: string;
  maxSizeBytes?: number;
  multiple?: boolean;
  formatErrorMessage?: (error: {
    type: FileUploadErrorType;
    file: File;
  }) => ReactNode;
  onFileAccepted?: (file: File, controls: FileUploadControls) => void;
}

interface FileUploadRefs {
  inputRef: RefObject<HTMLInputElement | null>;
}

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB

export default class FileUploadCubit extends Cubit<FileUploadState> {
  private config: Required<
    Pick<FileUploadConfig, "accept" | "maxSizeBytes" | "multiple">
  > &
    Pick<FileUploadConfig, "formatErrorMessage" | "onFileAccepted"> = {
    accept: "*",
    maxSizeBytes: DEFAULT_MAX_SIZE,
    multiple: false
  };

  private refs?: FileUploadRefs;

  constructor() {
    super({ files: [] });
  }

  setConfig = (config: FileUploadConfig) => {
    if (config.accept !== undefined) {
      this.config.accept = config.accept;
    }
    if (config.maxSizeBytes !== undefined) {
      this.config.maxSizeBytes = config.maxSizeBytes;
    }
    if (config.multiple !== undefined) {
      this.config.multiple = config.multiple;
    }
    if (config.formatErrorMessage !== undefined) {
      this.config.formatErrorMessage = config.formatErrorMessage;
    }
    if (config.onFileAccepted !== undefined) {
      this.config.onFileAccepted = config.onFileAccepted;
    }
  };

  setRefs = (refs: FileUploadRefs) => {
    this.refs = refs;
  };

  private isSameFile = (a: File, b: File) =>
    a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

  addFiles = (selectedFiles: File[]) => {
    const { accept, maxSizeBytes } = this.config;

    const newEntries: {
      file: File;
      error?: { type: FileUploadErrorType; message?: ReactNode };
    }[] = [];

    for (const file of selectedFiles) {
      const alreadyAdded =
        this.state.files.some((f) => this.isSameFile(f, file)) ||
        newEntries.some((e) => this.isSameFile(e.file, file));
      if (alreadyAdded) {
        continue;
      }

      if (accept !== "*") {
        const acceptedTypes = accept.split(",").map((t) => t.trim());
        const isValidType = acceptedTypes.some((type) => {
          if (type.startsWith(".")) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.endsWith("/*")) {
            return file.type.startsWith(type.slice(0, -2));
          }
          return file.type === type;
        });

        if (!isValidType) {
          const message = this.config.formatErrorMessage?.({
            type: "type",
            file
          });
          newEntries.push({ file, error: { type: "type", message } });
          continue;
        }
      }

      if (file.size > maxSizeBytes) {
        const message = this.config.formatErrorMessage?.({
          type: "size",
          file
        });
        newEntries.push({ file, error: { type: "size", message } });
        continue;
      }

      newEntries.push({ file });
    }

    const prevFiles = this.state.files;
    const prevErrors = this.state.fileErrors;

    const files = [...prevFiles, ...newEntries.map((e) => e.file)];
    const errorMap = new Map(prevErrors);
    newEntries.forEach((entry, i) => {
      if (entry.error) {
        errorMap.set(prevFiles.length + i, entry.error);
      }
    });

    this.emit({
      files,
      fileErrors: errorMap.size > 0 ? errorMap : undefined,
      fileStatuses: this.state.fileStatuses
    });

    if (this.config.onFileAccepted) {
      for (const entry of newEntries) {
        if (entry.error) {
          continue;
        }
        this.config.onFileAccepted(entry.file, this.createControls(entry.file));
      }
    }
  };

  private createControls = (file: File): FileUploadControls => ({
    setStatus: (status) => {
      const idx = this.state.files.indexOf(file);
      if (idx === -1) {
        return;
      }
      this.setFileStatus(idx, status);
    },
    setErrorMessage: (message) => {
      const idx = this.state.files.indexOf(file);
      if (idx === -1) {
        return;
      }
      this.setFileError(idx, { type: "upload", message });
    },
    setProgress: () => {
      // future use
    }
  });

  private setFileError = (
    index: number,
    error: { type: FileUploadErrorType; message?: ReactNode }
  ) => {
    const errors = new Map(this.state.fileErrors);
    errors.set(index, error);
    this.patch({ fileErrors: errors });
  };

  setFileStatus = (index: number, status: FileUploadStatus) => {
    const statuses = new Map(this.state.fileStatuses);
    statuses.set(index, status);
    this.patch({ fileStatuses: statuses });
  };

  removeFile = (index: number) => {
    const files = this.state.files.filter((_, i) => i !== index);

    const newErrors = new Map<
      number,
      { type: FileUploadErrorType; message?: ReactNode }
    >();
    this.state.fileErrors?.forEach((error, i) => {
      if (i < index) {
        newErrors.set(i, error);
      } else if (i > index) {
        newErrors.set(i - 1, error);
      }
    });

    const newStatuses = new Map<number, FileUploadStatus>();
    this.state.fileStatuses?.forEach((s, i) => {
      if (i < index) {
        newStatuses.set(i, s);
      } else if (i > index) {
        newStatuses.set(i - 1, s);
      }
    });

    this.emit({
      files,
      fileErrors: newErrors.size > 0 ? newErrors : undefined,
      fileStatuses: newStatuses.size > 0 ? newStatuses : undefined
    });
  };

  handleDrop = (e: DropEvent) => {
    const fileItems = e.items.filter(
      (item): item is FileDropItem => item.kind === "file"
    );

    void Promise.all(fileItems.map((item) => item.getFile())).then(
      (droppedFiles) => {
        const filesToProcess = this.config.multiple
          ? droppedFiles
          : droppedFiles.slice(0, 1);
        this.addFiles(filesToProcess);
      }
    );
  };

  handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) {
      return;
    }
    this.addFiles(Array.from(fileList));
    if (this.refs?.inputRef.current) {
      this.refs.inputRef.current.value = "";
    }
  };

  handleRemove = (_file: File, index: number) => {
    this.removeFile(index);
  };

  get showDropzone() {
    return this.config.multiple || this.state.files.length === 0;
  }

  get accept() {
    return this.config.accept;
  }

  get multiple() {
    return this.config.multiple;
  }
}
